import {CustomDomainScreenContext} from './base';
import * as React from 'react';
import {memo, useContext, useState} from 'react';
import {SimpleTable} from '../../ui-components/table/SimpleTable';
import {Row} from '@tanstack/react-table';
import {CustomDomain, CustomDomainState} from '../../api/types/organization/CustomDomain';
import {PlusNoColorIcon} from '../../ui-components/Iconpack';
import {Button} from '../../ui-components/Button/button';
import {Link, useNavigate} from 'react-router-dom';
import {Dialog, DialogContent} from '../../ui-components/Modal';
import {StorageIcon} from '../RemoteStorage/screen';
import {StorageType} from '../../api/types/organization/StorageSource';
import {ArrowLeft, TrashIcon} from 'lucide-react';

const RenderState = memo((props: {state: CustomDomainState}) => {
    switch (props.state) {
        case CustomDomainState.CNAME_PENDING:
            return <span className={'text-red-500'}>Unverified</span>;
        case CustomDomainState.CNAME_VERIFIED:
            return <span className={'text-orange-500'}>Provisioning</span>;
        case CustomDomainState.DOMAIN_PENDING:
            return <span className={'text-orange-500'}>Provisioning</span>;
        case CustomDomainState.DOMAIN_VERIFIED:
            return <span className={'text-orange-500'}>Provisioning</span>;
        case CustomDomainState.DOMAIN_READY:
            return <span className={'text-green-700'}>Verified</span>;
        case CustomDomainState.DOMAIN_ERROR:
            return <span className={'text-red-500'}>Failed</span>;
    }
});

export function BoxWithTitle(props: {title: string; children?: React.ReactNode}) {
    const navigate = useNavigate();

    return (
        <div className={'m-8 rounded-lg border border-gray-200'}>
            <div className='p-6'>
                <div className={'flex flex-row items-center'}>
                    <ArrowLeft className={'mr-2 h-6 w-6 cursor-pointer'} onClick={() => navigate(-1)} />
                    <span className='text-xl font-bold text-black'>{props.title}</span>
                </div>
            </div>
            <hr />
            {!!props.children ? props.children : null}
        </div>
    );
}

export default function CustomDomainList() {
    const {list} = useContext(CustomDomainScreenContext);
    const navigate = useNavigate();

    const [errorIdx, setErrorIdx] = useState<number>(-1);

    return (
        <>
            <Dialog open={errorIdx >= 0} onOpenChange={(open) => (open ? setErrorIdx(errorIdx) : setErrorIdx(-1))}>
                <DialogContent className={'max-w-md'} onClose={() => setErrorIdx(-1)}>
                    <div className={'text-lg font-bold'}>
                        Error Details ({errorIdx >= 0 ? list[errorIdx].hostname : null})
                    </div>
                    <div className={'text-sm'}>{errorIdx >= 0 ? list[errorIdx].metadata.error : null}</div>
                </DialogContent>
            </Dialog>
            <div className={'my-[30px] rounded-[12px] border border-[#EAECF0] md:mx-[40px]'}>
                <div className='px-[1.5rem] py-[1.5rem] pb-[1.5rem]'>
                    <div className={'flex flex-row items-center'}>
                        <span className='text-[18px] font-[700] text-[#000000]'>Custom Domains</span>
                        <div className={'flex-end ml-auto'}>
                            <Button
                                variant='outline'
                                size={'sm'}
                                icon
                                onClick={() => {
                                    navigate('add');
                                }}>
                                <PlusNoColorIcon />
                                Add New
                            </Button>
                        </div>
                    </div>
                </div>
                <hr />
                {list.length > 0 ? (
                    <SimpleTable
                        className={'w-full'}
                        columns={[
                            {
                                id: 'hostname',
                                header: 'Hostname',
                                cell: ({row}: {row: Row<CustomDomain>}) => {
                                    return <>{row.original.hostname}</>;
                                },
                            },
                            {
                                id: 'organization_storage_source',
                                header: 'Storage Source',
                                cell: ({row}: {row: Row<CustomDomain>}) => {
                                    const oss = row.original.organization_storage_source;
                                    if (!!oss) {
                                        return (
                                            <Link
                                                to={'../remote-storage?name=' + oss.name}
                                                className={'flex flex-row items-center text-blue-500 hover:underline'}>
                                                <StorageIcon className={'h-4 w-4'} type={oss.type} />
                                                <span className={'ml-1'}>{oss.name}</span>
                                            </Link>
                                        );
                                    } else {
                                        return (
                                            <div className={'flex flex-row items-center'}>
                                                <StorageIcon className={'h-4 w-4'} type={StorageType.DEFAULT_STORAGE} />
                                                <span className={'ml-1 italic'}>default</span>
                                            </div>
                                        );
                                    }
                                },
                            },
                            {
                                id: 'state',
                                header: 'State',
                                cell: ({row}: {row: Row<CustomDomain>}) => {
                                    return (
                                        <span className={'font-bold'}>
                                            <RenderState state={row.original.state} />
                                        </span>
                                    );
                                },
                            },
                            {
                                id: 'action',
                                cell: ({row}: {row: Row<CustomDomain>}) => {
                                    return (
                                        <div className={'flex flex-row text-base'}>
                                            <div className={'flex-end ml-auto flex flex-row gap-2'}>
                                                {row.original.state === CustomDomainState.CNAME_PENDING ? (
                                                    <Button
                                                        variant={'outline'}
                                                        size={'sm'}
                                                        onClick={() => {
                                                            navigate('verify/' + row.original.hostname);
                                                        }}>
                                                        Verify
                                                    </Button>
                                                ) : row.original.state === CustomDomainState.CNAME_VERIFIED ||
                                                  row.original.state === CustomDomainState.DOMAIN_PENDING ||
                                                  row.original.state === CustomDomainState.DOMAIN_VERIFIED ? (
                                                    <Button
                                                        variant={'outline'}
                                                        size={'sm'}
                                                        onClick={() => {
                                                            navigate('verify/' + row.original.hostname);
                                                        }}>
                                                        Status
                                                    </Button>
                                                ) : row.original.state === CustomDomainState.DOMAIN_READY ? (
                                                    <Button
                                                        variant={'outline'}
                                                        size={'sm'}
                                                        onClick={() => {
                                                            navigate('settings/' + row.original.hostname);
                                                        }}>
                                                        Manage
                                                    </Button>
                                                ) : row.original.state === CustomDomainState.DOMAIN_ERROR ? (
                                                    <Button
                                                        variant={'outline'}
                                                        size={'sm'}
                                                        onClick={() => {
                                                            setErrorIdx(row.index);
                                                        }}>
                                                        Details
                                                    </Button>
                                                ) : null}
                                                <Button
                                                    variant={'outline'}
                                                    size={'sm'}
                                                    onClick={() => {
                                                        navigate('delete/' + row.original.hostname);
                                                    }}
                                                    icon
                                                    title={'Delete'}
                                                    className={'text-red-500'}>
                                                    <TrashIcon className={'h-4 w-4'} />
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                },
                            },
                        ]}
                        data={list}
                    />
                ) : (
                    <div className={'p-4 text-center text-gray-500'}>No Custom Domains added</div>
                )}
            </div>
        </>
    );
}
